import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { syncNotificationState } from './notification-sync';

/**
 * Comprehensive notification debugging and manual sync utility
 * This helps diagnose and fix notification permission sync issues
 */
export const debugNotificationSync = async (): Promise<{
  success: boolean;
  details: any;
  error?: string;
}> => {
  try {
    

    if (!Capacitor.isNativePlatform()) {
      return {
        success: false,
        details: { platform: 'web' },
        error: 'Not on native platform'
      };
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: false,
        details: {},
        error: 'No authenticated user'
      };
    }

    

    // Get current user data from database
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id, email, name, onesignal_user_id, notification_enabled, notification_permission_asked')
      .eq('id', user.id)
      .single();

    if (userError) {
      return {
        success: false,
        details: { userError },
        error: 'Failed to fetch user data'
      };
    }

    // Check OneSignal adapter
    const oneSignalModule = await import('@/integrations/onesignal');
    const getOneSignalAdapter = (oneSignalModule as any).getOneSignalAdapter;
    const adapter = getOneSignalAdapter();

    let adapterInfo = {
      available: adapter?.isAvailable() || false,
      deviceState: null,
      permissionStatus: null
    };

    if (adapter?.isAvailable()) {
      try {
        adapterInfo.deviceState = await adapter.getDeviceState();
      } catch (error) {
        
      }
    }

    // Check native permissions
    let nativePermissions = null;
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      nativePermissions = await LocalNotifications.checkPermissions();
    } catch (error) {
      
    }

    // Run the actual sync
    const syncResult = await syncNotificationState(user.id);

    // Get updated user data after sync
    const { data: updatedUserData } = await supabase
      .from('users')
      .select('onesignal_user_id, notification_enabled, notification_permission_asked')
      .eq('id', user.id)
      .single();

    const debugInfo = {
      user: {
        id: user.id,
        email: userData.email,
        name: userData.name
      },
      beforeSync: {
        onesignal_user_id: userData.onesignal_user_id,
        notification_enabled: userData.notification_enabled,
        notification_permission_asked: userData.notification_permission_asked
      },
      afterSync: {
        onesignal_user_id: updatedUserData?.onesignal_user_id,
        notification_enabled: updatedUserData?.notification_enabled,
        notification_permission_asked: updatedUserData?.notification_permission_asked
      },
      oneSignal: adapterInfo,
      nativePermissions,
      syncResult,
      timestamp: new Date().toISOString()
    };

    

    return {
      success: true,
      details: debugInfo
    };

  } catch (error) {
    
    return {
      success: false,
      details: {},
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Manual fix for notification sync issues
 * This forces the notification_enabled to true if the user has a OneSignal ID
 */
export const manualFixNotificationStatus = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: false,
        message: 'No authenticated user'
      };
    }

    // Get current user data
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('onesignal_user_id, notification_enabled, notification_permission_asked')
      .eq('id', user.id)
      .single();

    if (userError) {
      return {
        success: false,
        message: 'Failed to fetch user data'
      };
    }

    // If user has OneSignal ID but notification_enabled is false, fix it
    if (userData.onesignal_user_id && !userData.notification_enabled) {
      const { error: updateError } = await supabase
        .from('users')
        .update({
          notification_enabled: true,
          notification_permission_asked: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        return {
          success: false,
          message: 'Failed to update notification status'
        };
      }

      return {
        success: true,
        message: 'Successfully fixed notification status - set to enabled',
        details: {
          onesignal_user_id: userData.onesignal_user_id,
          previous_notification_enabled: userData.notification_enabled,
          new_notification_enabled: true
        }
      };
    } else if (!userData.onesignal_user_id) {
      return {
        success: false,
        message: 'User does not have OneSignal ID - cannot automatically enable notifications'
      };
    } else {
      return {
        success: true,
        message: 'Notification status is already correct',
        details: {
          onesignal_user_id: userData.onesignal_user_id,
          notification_enabled: userData.notification_enabled
        }
      };
    }

  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Enhanced manual fix for notification sync issues
 * This not only fixes database state but also re-requests actual device permissions
 */
export const enhancedManualFixNotificationStatus = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
  requiresSettingsRedirect?: boolean;
}> => {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: false,
        message: 'No authenticated user'
      };
    }

    // Get current user data
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('onesignal_user_id, notification_enabled, notification_permission_asked')
      .eq('id', user.id)
      .single();

    if (userError) {
      return {
        success: false,
        message: 'Failed to fetch user data'
      };
    }

    // Get OneSignal adapter
    const oneSignalModule = await import('@/integrations/onesignal');
    const getOneSignalAdapter = (oneSignalModule as any).getOneSignalAdapter;
    const adapter = getOneSignalAdapter();

    if (!adapter?.isAvailable()) {
      return {
        success: false,
        message: 'OneSignal adapter not available'
      };
    }

    // Check current device state
    let deviceState: any = null;
    try {
      deviceState = await adapter.getDeviceState();
    } catch (error) {
      return {
        success: false,
        message: 'Failed to get device state'
      };
    }

    // Check native permissions as a backup
    let nativePermission = false;
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const permissionStatus = await LocalNotifications.checkPermissions();
      nativePermission = permissionStatus.display === 'granted';
    } catch (error) {
      
    }

    const hasDevicePermission = deviceState?.hasNotificationPermission || nativePermission;

    

    // If device already has permission, just sync the database
    if (hasDevicePermission) {
      const { error: updateError } = await supabase
        .from('users')
        .update({
          notification_enabled: true,
          notification_permission_asked: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        return {
          success: false,
          message: 'Failed to update notification status'
        };
      }

      return {
        success: true,
        message: 'Device already has permission - database updated successfully',
        details: {
          devicePermission: hasDevicePermission,
          databaseUpdated: true
        }
      };
    }

    // Device doesn't have permission - try to request it
    
    
    let permissionGranted = false;
    try {
      permissionGranted = await adapter.promptForPushNotificationsWithUserResponse(true);
      
    } catch (error) {
      
    }

    // Update database with the result
    const { error: updateError } = await supabase
      .from('users')
      .update({
        notification_enabled: permissionGranted,
        notification_permission_asked: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id);

    if (updateError) {
      return {
        success: false,
        message: 'Failed to update notification status'
      };
    }

    if (permissionGranted) {
      // Get updated device state to sync OneSignal ID
      try {
        const updatedDeviceState = await adapter.getDeviceState();
        if (updatedDeviceState?.userId) {
          await supabase
            .from('users')
            .update({
              onesignal_user_id: updatedDeviceState.userId,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);
        }
      } catch (error) {
        
      }

      return {
        success: true,
        message: 'Permissions granted successfully! Notifications are now enabled.',
        details: {
          permissionGranted,
          databaseUpdated: true
        }
      };
    } else {
      return {
        success: false,
        message: 'Permission denied. To enable notifications, please go to iOS Settings > [Your App Name] > Notifications and enable them manually.',
        requiresSettingsRedirect: true,
        details: {
          permissionGranted,
          databaseUpdated: true,
          needsManualSettings: true
        }
      };
    }

  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}; 